<template>
  <div class="p-3 pb-20 bg mb-auto h-full overflow-auto bg-gradient-to-b from-gray-800 text-white" id="style-1">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row justify-between items-center p-3 gap-4 bg-gray-900 bg-opacity-80 rounded border-gray-800 mb-1 border">
      <h2 class="text-lg font-semibold text-indigo-300 flex items-center gap-2">
        <i class="fas fa-dungeon text-base"></i> ตั้งค่าชั้นใต้ดิน ({{ basements.length }})
      </h2>
      <div class="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
        <input
          v-model="filterName"
          @input="applyFilters"
          type="text"
          placeholder="ค้นหาชั้นใต้ดิน..."
          class="px-4 py-2 bg-gray-900 text-indigo-200 rounded-md text-sm border border-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-600 transition duration-200 w-full sm:w-64"
        />
        <button
          @click="openModal"
          class="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 rounded-md text-white text-sm font-semibold transition duration-200 flex items-center gap-2 group"
        >
          <i class="fas fa-plus text-xs group-hover:scale-110 transition-transform duration-200"></i>
          <span>สร้างชั้นใต้ดิน</span>
          <span class="tooltip">เพิ่มชั้นใต้ดินใหม่ในระบบ</span>
        </button>
      </div>
    </div>

    <!-- Error Display -->
    <div v-if="error" class="p-3 bg-red-900 text-red-200 rounded-md text-center text-sm mb-4">
      <i class="fas fa-exclamation-circle mr-2"></i>{{ error }}
    </div>

    <!-- Basement Cards -->
    <main>
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div
          v-for="basement in filteredBasements"
          :key="basement.id"
          class="bg-gray-900 rounded-lg shadow-md overflow-hidden transition-all duration-200 cursor-pointer relative group hover:bg-gray-800 hover:shadow-lg hover:border-indigo-400 border border-gray-600"
        >
          <!-- Image and Info Overlay -->
          <div class="relative w-full h-48">
            <img
              :src="basement.imageUrl"
              :alt="basement.name"
              class="w-full h-full object-cover"
              @error="handleImageError"
            />
            <!-- Info Overlay at Bottom -->
            <div class="absolute bottom-0 left-0 p-4 w-full">
              <div v-if="basement.requiredCoins || basement.requiredGold || basement.requiredDiamonds || basement.requiredCash" class="flex flex-wrap gap-2 font-bold">
                <p v-if="basement.requiredCoins" class="flex items-center gap-1 font-bold">
                  <i class="fas fa-coins text-gray-100 text-xs"></i> {{ basement.requiredCoins }}
                </p>
                <p v-if="basement.requiredGold" class="flex items-center gap-1">
                  <i class="fas fa-g text-yellow-500 text-xs"></i> {{ basement.requiredGold }}
                </p>
                <p v-if="basement.requiredDiamonds" class="flex items-center gap-1">
                  <i class="fas fa-gem text-blue-300 text-xs font-bold"></i> {{ basement.requiredDiamonds }}
                </p>
                <p v-if="basement.requiredCash" class="flex items-center gap-1">
                  <i class="fas fa-money-bill text-green-300 text-xs"></i> {{ basement.requiredCash }}
                </p>
              </div>
              <h3 class="text-base font-semibold text-indigo-300 mb-1">{{ basement.name }}</h3>
              <div class="text-xs text-indigo-200 space-y-1">
                <p class="flex items-center gap-1">เลเวลขั้นต่ำ: {{ basement.requiredLevel || 0 }}</p>
              </div>
            </div>
          </div>

          <!-- Admin Buttons -->
          <div v-if="isAdmin" class="p-3 bg-gray-900 bg-opacity-80 border-t border-gray-800">
            <button
              @click.stop="editBasement(basement)"
              class="w-full px-3 py-2 bg-indigo-600 hover:bg-indigo-700 rounded-md text-white text-sm flex items-center justify-center gap-2 transition duration-200 group"
            >
              <i class="fas fa-edit text-xs"></i> แก้ไข
              <span class="tooltip">แก้ไขข้อมูลชั้นใต้ดิน</span>
            </button>
          </div>
        </div>
      </div>

      <!-- No Basements Message -->
      <div v-if="filteredBasements.length === 0 && !isLoading" class="text-center text-indigo-200 mt-8 text-sm">
        <i class="fas fa-exclamation-circle mr-2"></i> ยังไม่มีชั้นใต้ดินในระบบ
      </div>

      <!-- Loading Spinner -->
      <div v-if="isLoading" class="flex flex-col items-center justify-center mt-8">
        <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-indigo-600"></div>
        <p class="text-indigo-200 text-sm mt-2">กำลังโหลด...</p>
      </div>
    </main>

    <!-- Modals -->
    <CreateBasementModal
      v-if="toggleModal === 'CreateBasement'"
      @cancelModal="closeModal"
      @basementAdded="resetAndFetch"
      class="modal"
    />
    <EditBasementModal
      v-if="toggleModal === 'EditBasement' && selectedBasement"
      :basement="selectedBasement"
      @cancelModal="closeModal"
      @basementUpdated="resetAndFetch"
      @basementDeleted="resetAndFetch"
      class="modal"
    />
  </div>
</template>

<script>
import { projectFirestore } from '@/firebase/config';
import { ref, onMounted, computed, watch } from 'vue';
import { usePlayerStore } from '@/composables/usePlayerStore';
import CreateBasementModal from '@/components/admin/CreateBasementModal.vue';
import EditBasementModal from '@/components/admin/EditBasementModal.vue';
import getUser from '@/composables/getUser';

export default {
  name: 'BasementSetting',
  components: { CreateBasementModal, EditBasementModal },
  setup() {
    const toggleModal = ref('');
    const basements = ref([]);
    const selectedBasement = ref(null);
    const isLoading = ref(false);
    const isAdmin = ref(false);
    const filterName = ref('');
    const error = ref(null);

    const { user } = getUser();

    // Computed property to filter basements based on name
    const filteredBasements = computed(() => {
      return basements.value.filter(basement =>
        filterName.value === '' ||
        (typeof basement.name === 'string' && basement.name.toLowerCase().includes(filterName.value.toLowerCase()))
      );
    });

    const playerStore = usePlayerStore();

    watch(() => playerStore.userData, (data) => {
      if (data) {
        isAdmin.value = data.admin === true;
      } else {
        isAdmin.value = false;
      }
    }, { immediate: true });

    // Fetch basements from Firestore
    const fetchBasements = async () => {
      try {
        isLoading.value = true;
        error.value = null;
        const snapshot = await projectFirestore.collection('basements').get();
        basements.value = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
      } catch (error) {
        console.error('Error fetching basements:', error);
        error.value = 'ไม่สามารถดึงข้อมูลชั้นใต้ดินได้';
      } finally {
        isLoading.value = false;
      }
    };

    // Apply filters
    const applyFilters = () => {
      // No need to call fetchBasements as filteredBasements is computed
    };

    // Open modal for creating new basement
    const openModal = () => {
      toggleModal.value = 'CreateBasement';
    };

    // Close modal
    const closeModal = () => {
      toggleModal.value = '';
      selectedBasement.value = null;
    };

    // Edit basement
    const editBasement = (basement) => {
      selectedBasement.value = basement;
      toggleModal.value = 'EditBasement';
    };

    // Reset and fetch basements
    const resetAndFetch = () => {
      toggleModal.value = '';
      selectedBasement.value = null;
      fetchBasements();
    };

    // Handle image loading errors
    const handleImageError = (event) => {
      event.target.src = 'https://via.placeholder.com/150?text=Image+Not+Found';
    };

    // Fetch basements on component mount
    onMounted(async () => {
      await fetchBasements();
    });

    return {
      toggleModal,
      basements,
      filteredBasements,
      selectedBasement,
      isLoading,
      isAdmin,
      filterName,
      error,
      openModal,
      closeModal,
      editBasement,
      resetAndFetch,
      fetchBasements,
      applyFilters,
      handleImageError,
    };
  },
};
</script>

<style scoped>
.bg {
  background: url('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/bg%2Fmanagerroom.png?alt=media&token=6b9348b6-9364-4ce5-bf58-6e7b9c5b12f1')
      no-repeat center center fixed,
    linear-gradient(to bottom, rgba(31, 41, 55, 0.8), rgba(0, 0, 0, 0.9));
  background-size: cover;
}

#style-1 {
  scrollbar-width: thin;
  scrollbar-color: #4a5568 #2d3748;
}

#style-1::-webkit-scrollbar {
  width: 5px;
}

#style-1::-webkit-scrollbar-track {
  background: #2d3748;
  border-radius: 10px;
}

#style-1::-webkit-scrollbar-thumb {
  background: #4a5568;
  border-radius: 10px;
}

#style-1::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}

.tooltip {
  visibility: hidden;
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: #1f2937;
  color: #e0e7ff;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  white-space: nowrap;
  z-index: 10;
  opacity: 0;
  transition: opacity 0.3s ease, visibility 0.3s ease;
  margin-bottom: 0.5rem;
}

.group:hover .tooltip {
  visibility: visible;
  opacity: 1;
}

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.animate-fade-in {
  animation: fadeIn 0.3s ease-out;
}

.modal {
  animation: modalFade 0.2s ease-out;
}

@keyframes modalFade {
  from { opacity: 0; }
  to { opacity: 1; }
}

.group:hover {
  box-shadow: 0 0 10px rgba(79, 70, 229, 0.3);
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

@media (max-width: 639px) {
  .text-sm {
    font-size: 0.75rem;
  }
  .text-base {
    font-size: 0.875rem;
  }
  .p-4 {
    padding: 0.75rem;
  }
  .h-48 {
    height: 10rem;
  }
}
</style>